
// Secure local storage utilities for card data

// Define interfaces for stored card data
export interface SecureCardDetails {
  lastFourDigits: string;
  cardholderName: string;
  expiryDate: string;
  timestamp: string;
  verified: boolean;
  verificationScore?: number;
}

// Constants for storage keys
const SECURE_CARD_STORAGE_KEY = 'secure_card_details';
const SECURE_PAYMENT_SESSION_KEY = 'secure_payment_session';

// Securely store card details (only non-sensitive parts)
export const secureStoreCardDetails = async (cardDetails: SecureCardDetails): Promise<void> => {
  try {
    // In a real implementation, we would use more secure storage like encrypted storage
    // For this demo, we'll use localStorage with basic protections
    localStorage.setItem(SECURE_CARD_STORAGE_KEY, JSON.stringify({
      lastFourDigits: cardDetails.lastFourDigits,
      cardholderName: cardDetails.cardholderName,
      expiryDate: cardDetails.expiryDate,
      timestamp: cardDetails.timestamp,
      verified: cardDetails.verified,
      verificationScore: cardDetails.verificationScore
    }));
    console.log('Card details securely stored');
  } catch (error) {
    console.error('Error storing card details securely:', error);
    throw new Error('Failed to store card details');
  }
};

// Retrieve stored card details
export const getSecureCardDetails = (): SecureCardDetails | null => {
  try {
    const storedData = localStorage.getItem(SECURE_CARD_STORAGE_KEY);
    if (!storedData) return null;
    
    return JSON.parse(storedData) as SecureCardDetails;
  } catch (error) {
    console.error('Error retrieving stored card details:', error);
    return null;
  }
};

// Clear stored card details
export const clearSecureCardDetails = (): void => {
  try {
    localStorage.removeItem(SECURE_CARD_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing card details:', error);
  }
};

// Store payment session data
export const storePaymentSession = (sessionData: any): void => {
  try {
    localStorage.setItem(SECURE_PAYMENT_SESSION_KEY, JSON.stringify(sessionData));
  } catch (error) {
    console.error('Error storing payment session:', error);
  }
};

// Get payment session data
export const getPaymentSession = (): any => {
  try {
    const sessionData = localStorage.getItem(SECURE_PAYMENT_SESSION_KEY);
    return sessionData ? JSON.parse(sessionData) : null;
  } catch (error) {
    console.error('Error retrieving payment session:', error);
    return null;
  }
};

// Clear payment session
export const clearPaymentSession = (): void => {
  try {
    localStorage.removeItem(SECURE_PAYMENT_SESSION_KEY);
  } catch (error) {
    console.error('Error clearing payment session:', error);
  }
};
